import { ComponentPropsWithoutRef, forwardRef } from "react";
import { css, keyframes } from "@emotion/react";
import { FieldGroupContextValue, useFieldGroup } from "../FieldGroup";
import { ShimmerEffect, ShimmerEffectBox } from "../ShimmerEffect";
import { useClickAnimation } from "../hooks/useClickAnimation";
import { useTheme } from "../theme";

export interface CheckboxProps
  extends ComponentPropsWithoutRef<"input">, FieldGroupContextValue {
  /**
   * ラベルの表示に利用できます。
   */
  label?: React.ReactNode;
  /**
   * ラベルの表示に利用できます
   * @deprecated 代わりにlabelを利用してください。
   * childrenがlabelであることや、input要素のようなコンポーネントがchildrenを持っていることに違和感があるため、非推奨になりました。
   */
  children?: React.ReactNode;
  /**
   * 値をコントロールする場合の値を指定します。checkedを使用する場合、onChangeは必須です。
   */
  checked?: boolean;
  /**
   * 値をアンコントロールする場合の初期値を指定します。値の取得はform要素を介して行ってください。
   * @default false
   */
  defaultChecked?: boolean;
  disabled?: boolean;
  /**
   * @experimental disabled時の挙動などが未定義です。
   */
  indeterminate?: boolean;
}

const check = keyframes`
    0%{
        transform: scale(0.5) rotate(45deg) translate(-1.5px, -1.5px);
    }
    100%{
        transform: scale(1) rotate(45deg) translate(-1.5px, -1.5px);
    }
`;
const fade = keyframes`
  100% {
      opacity: 0;
  }
`;
const wave = keyframes`
  100% {
      box-shadow: 0 0 0 6px currentcolor;
  }
`;

export const Checkbox = forwardRef<HTMLLabelElement, CheckboxProps>(
  (
    {
      children,
      label: propsLabel,
      style,
      className,
      onClick: propsOnClick,
      loading: propsLoading,
      readOnly: propsReadOnly,
      disabled,
      required,
      indeterminate,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const label = propsLabel ?? children;
    const hasLabel = label !== null && label !== undefined;
    const RootComponent = hasLabel ? "label" : "span";
    const { clicked, onClick, onAnimationEnd } =
      useClickAnimation(propsOnClick);
    const { loading, readOnly } = useFieldGroup({
      loading: propsLoading,
      readOnly: propsReadOnly,
    });
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const disabledOrReadonly = disabled || readOnly;
    return (
      <RootComponent
        ref={ref}
        css={css`
          position: relative;
          box-sizing: border-box;
          display: inline-flex;
          align-items: center;
        `}
        style={style}
        className={className}
      >
        <div
          css={[
            css`
              padding: 8px;
            `,
            disabled
              ? css`
                  cursor: not-allowed;
                  background-color: ${theme.palette.gray[300]};
                  border-radius: 4px;
                `
              : css`
                  cursor: pointer;
                `,
            readOnly
              ? css`
                  background-color: ${theme.palette.gray[300]};
                  border-radius: 4px;
                `
              : null,
          ]}
        >
          {loading ? (
            <ShimmerEffectBox width={24} height={24} borderRadius={4} />
          ) : (
            <input
              onAnimationEnd={onAnimationEnd}
              data-clicked={clicked}
              type="checkbox"
              /**
               * type=checkboxのinput要素には、readOnly属性が存在しないため
               * aria-readonlyとdisabledの併用で代替しています。
               */
              aria-readonly={readOnly}
              onClick={onClick}
              required={required}
              css={[
                css`
                  --checkbox-size: 24px;
                  position: relative;
                  box-sizing: border-box;
                  display: flex;
                  flex-shrink: 0;
                  align-items: center;
                  justify-content: center;
                  width: var(--checkbox-size);
                  height: var(--checkbox-size);
                  padding: 0px;
                  margin: 0px;
                  color: ${theme.palette.intentions.primary.contrastText};
                  touch-action: manipulation;
                  cursor: pointer;
                  background-color: white;
                  border: 2px solid ${theme.palette.gray[700]};
                  border-radius: 4px;
                  outline: none;
                  transition: background-color
                    ${theme.transitions.duration.shortest}
                    ${theme.transitions.easing.easeIn};
                  appearance: none;

                  &:checked {
                    background-color: ${theme.palette.intentions.primary.main};
                    border-color: ${theme.palette.intentions.primary.main};
                  }

                  &::before {
                    box-sizing: border-box;
                    display: block;
                    width: 0.55em;
                    height: 1em;
                    content: "";
                    border-right: 3px solid white;
                    border-bottom: 3px solid white;
                    opacity: 0;
                    transform: rotate(45deg) scale(1);
                  }
                  &:checked {
                    &::before {
                      opacity: 1;
                      transform: rotate(45deg) scale(1)
                        translate(-1.5px, -1.5px);
                      animation: ${check} 0.36s
                        cubic-bezier(0.12, 0.4, 0.29, 1.46);
                    }
                  }

                  &:disabled {
                    color: ${theme.palette.action.disabled};
                    cursor: not-allowed;
                    background-color: ${theme.palette.gray[300]};
                    border-color: ${theme.palette.gray[400]};
                    &:checked {
                      background-color: ${theme.palette.gray[300]};
                      border-color: ${theme.palette.gray[400]};
                      &::before {
                        border-color: ${theme.palette.gray[400]};
                      }
                    }
                  }

                  &:disabled[aria-readonly="true"] {
                    background-color: white;
                    &:checked {
                      background-color: ${theme.palette.gray[500]};
                      border-color: ${theme.palette.gray[500]};
                      &::before {
                        border-color: white;
                      }
                    }
                  }

                  &::after {
                    position: absolute;
                    top: -1px;
                    right: -1px;
                    bottom: -1px;
                    left: -1px;
                    z-index: 1;
                    display: block;
                    color: ${theme.palette.intentions.primary.main};
                    pointer-events: none;
                    content: "";
                    border-radius: inherit;
                    opacity: 0.2;
                    transform: scale(1);
                  }
                  &[data-clicked="true"]::after {
                    animation:
                      ${fade} 2s cubic-bezier(0.08, 0.82, 0.17, 1),
                      ${wave} 0.4s cubic-bezier(0.08, 0.82, 0.17, 1);
                  }
                `,
                indeterminate
                  ? css`
                      background-color: ${theme.palette.intentions.primary
                        .main};
                      border-color: ${theme.palette.intentions.primary.main};
                      &::before {
                        width: 1em;
                        height: 0px;
                        border-right: 0px solid white;
                        border-bottom: 3px solid white;
                        opacity: 1;
                        transform: rotate(0deg) scale(1);
                      }
                      &:checked {
                        &::before,
                        &::after {
                          transform: none;
                          animation: none;
                        }
                      }
                    `
                  : null,
              ]}
              {...props}
              disabled={disabledOrReadonly}
            />
          )}
        </div>
        {hasLabel ? (
          <ShimmerEffect active={loading}>
            <span
              css={[
                theme.typography.body,
                css`
                  margin-left: 8px;
                  line-height: 1em;
                  white-space: inherit;
                `,
                {
                  color: disabled ? theme.palette.action.disabled : "inherit",
                  cursor: disabledOrReadonly ? "default" : "pointer",
                },
              ]}
            >
              {label}
              {required ? (
                <span
                  css={css`
                    color: ${theme.palette.intentions.error.main};
                  `}
                >
                  *
                </span>
              ) : null}
            </span>
          </ShimmerEffect>
        ) : null}
      </RootComponent>
    );
  },
);
